// The Mouse Codex of Cheese: a wide, clock-pausing book with chapters, entries, and quizzes.
import { CODEX, GEMS } from '../config/constants';
import { CHEESES, type ChapterId } from '../data/cheeses';
import { CHAPTERS, CODEX_BY_CHEESE, entriesInChapter, type CodexEntry } from '../data/codex';
import * as Codex from '../core/Codex';
import { retailBand } from '../core/Market';
import type { RenderResult } from './Panel';
import { $, fmt } from './panels';
import type { UI } from './UI';
import './book.css';
import { cheeseArt } from '../art/cheeseArt';

type Tab = 'story' | 'howto' | 'needs' | 'value' | 'quiz';
const TAB_LABEL: Record<Tab, string> = { story: 'History', howto: 'How to make', needs: 'Needs', value: 'Value', quiz: 'Quiz' };

let selected: string = 'mozzarella_fresh';
let tab: Tab = 'story';

export function openCodexPanel(ui: UI, cheeseId?: string): void {
  const game = ui.game;
  if (cheeseId) selected = cheeseId;
  ui.panels.open({
    id: 'codex',
    title: 'The Mouse Codex of Cheese',
    kind: 'pause',
    side: 'center',
    render(body, api): RenderResult {
      body.innerHTML = `<div class="book"><div class="book-binding" aria-hidden="true"></div><aside class="book-index"><div class="book-mark">MOUSE CODEX</div><div class="book-chapters"></div></aside><main class="book-page"></main></div>`;
      const chapters = $(body, '.book-chapters');
      const page = $(body, '.book-page');

      const renderChapters = (): void => {
        const s = game.state;
        chapters.innerHTML = `<div class="muted" style="margin-bottom:8px">Coins ${fmt(s.gold)} · Gems ${s.gems}</div>`;
        CHAPTERS.forEach((ch, i) => {
          const box = document.createElement('div');
          box.className = 'book-chapter';
          const revealed = Codex.chapterRevealed(s, ch.id);
          const title = document.createElement('div');
          title.className = `ch-title ${revealed ? '' : 'locked'}`;
          title.textContent = `${revealed ? '▱' : '▣'} ${i + 1}. ${revealed ? ch.title : '???'}`;
          title.title = ch.locked ?? `Discovery fee ${ch.fee} coins per cheese`;
          box.appendChild(title);
          if (!ch.locked) {
            for (const entry of entriesInChapter(ch.id)) {
              const row = document.createElement('button');
              const disc = Codex.isDiscovered(s, entry.cheeseId);
              row.className = `btn small book-entry ${selected === entry.cheeseId ? 'selected' : ''} ${Codex.isLearned(s, entry.cheeseId) ? 'learned' : ''} ${disc ? '' : 'undiscovered'}`;
              row.textContent = disc ? CHEESES[entry.cheeseId].name : '???';
              row.tabIndex = 0;
              row.setAttribute('role', 'button');
              row.setAttribute('aria-current', selected === entry.cheeseId ? 'page' : 'false');
              const choose = (): void => {
                if (game.quiz && !game.quiz.done) return;
                selected = entry.cheeseId;
                if (tab === 'quiz' && !disc) tab = 'story';
                api.rerender();
              };
              row.onclick = () => {
                choose();
              };
              row.onkeydown = (event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); choose(); } };
              box.appendChild(row);
            }
          } else {
            box.insertAdjacentHTML('beforeend', `<div class="book-entry undiscovered">${ch.locked}</div>`);
          }
          chapters.appendChild(box);
        });
      };

      const renderPage = (): void => {
        const s = game.state;
        const entry = CODEX_BY_CHEESE[selected];
        const def = CHEESES[selected];
        if (!entry || !def) {
          page.innerHTML = '<div class="muted">Pick an entry.</div>';
          return;
        }
        const disc = Codex.isDiscovered(s, selected);
        const learned = Codex.isLearned(s, selected);
        const chapterIndex = CHAPTERS.findIndex((c) => c.id === entry.chapter) + 1;
        if (!disc) {
          const block = Codex.discoveryBlock(s, selected);
          const fee = Codex.discoveryFee(selected);
          page.innerHTML = `
            <h3>???</h3>
            <div class="sub">Chapter ${chapterIndex} · Category ??? · Ageable ???</div>
            <p class="muted">Name: ??? · Process: ??? · Description: ???</p>
            <p>Pay the discovery fee to reveal this entry. Then read it and pass the quiz to learn the recipe.</p>
            <div class="row"><button class="btn primary discover">Discover this cheese · ${fee} coins</button><span class="muted dblock">${block ?? ''}</span></div>`;
          const btn = $<HTMLButtonElement>(page, '.discover');
          btn.disabled = !!block;
          btn.onclick = () => {
            if (game.discoverEntry(selected)) {
              tab = 'story';
              api.rerender();
            }
          };
          return;
        }
        const rule = def.aging;
        page.innerHTML = `
          <div class="entry-hero"><div><div class="eyebrow">FIELD PLATE · ${entry.category}</div><h3>${def.name}${learned ? ' <span class="good" style="font-size:14px">✓ learned</span>' : ''}</h3></div>${cheeseArt(def.id)}</div>
          <div class="sub">Chapter ${chapterIndex}: ${chapterTitle(entry.chapter)} · ${entry.category} · ${entry.origin} · ${rule.ageable ? `Ageable: yes (${rule.minAgeDays} day minimum)` : 'Ageable: no'}</div>
          <div class="book-tabs"></div>
          <div class="book-content"></div>`;
        const tabs = $(page, '.book-tabs');
        const content = $(page, '.book-content');
        (Object.keys(TAB_LABEL) as Tab[]).forEach((t) => {
          const b = document.createElement('button');
          b.className = `btn small ${tab === t ? 'active' : ''}`;
          b.textContent = t === 'quiz' ? `${TAB_LABEL[t]} (${Codex.gemsCollected(s, selected)}/${CODEX.QUESTIONS} gems)` : TAB_LABEL[t];
          b.onclick = () => {
            if (game.quiz && !game.quiz.done) return;
            tab = t;
            api.rerender();
          };
          tabs.appendChild(b);
        });
        switch (tab) {
          case 'story':
            content.innerHTML = entry.history.map((p) => `<p>${p}</p>`).join('');
            break;
          case 'howto':
            content.innerHTML = `<ol>${entry.howTo.map((st) => `<li>${st}</li>`).join('')}</ol>${def.process ? '' : '<p class="muted">This cheese is in the book for study. Its production chain arrives in a later build.</p>'}`;
            break;
          case 'needs':
            content.innerHTML = `<h4>Tree dependencies</h4><ul>${entry.dependencies.map((d) => `<li>${d}</li>`).join('')}</ul><h4>Ingredients</h4><ul>${entry.ingredients.map((d) => `<li>${d}</li>`).join('')}</ul>${needsStatus(game, def.id)}`;
            break;
          case 'value':
            content.innerHTML = renderValue(entry);
            break;
          case 'quiz':
            renderQuiz(content, entry);
            break;
        }
      };

      const chapterTitle = (id: ChapterId): string => CHAPTERS.find((c) => c.id === id)?.title ?? '';

      const needsStatus = (g: typeof game, id: string): string => {
        const block = g.recipeBlock(id);
        if (!CHEESES[id].process) return '';
        return `<p class="${block ? 'bad' : 'good'}">${block ?? 'You can make this cheese now.'}</p>`;
      };

      const renderValue = (entry: CodexEntry): string => {
        const d = CHEESES[entry.cheeseId];
        const band = retailBand(game.snapshot, d.marketKey);
        const lines = [
          band ? `<p><b>Market band today:</b> ${fmt(band.min)} to ${fmt(band.max)} coins per pound (${band.source}, as of ${band.asOf}). A ${d.unitPounds} lb ${d.unitLabel} at 3★ is about ${fmt(((band.min + band.max) / 2) * d.unitPounds)} coins before demand and channel.</p>` : '',
          `<p><b>Rarity:</b> ${d.rarity} (gem drops ×${GEMS.RARITY_MULT[d.rarity]}).</p>`,
          d.aging.ageable
            ? `<p><b>Aging curve:</b> ×1.0 until day ${d.aging.minAgeDays}, rising to ×${d.aging.peakMult} at day ${d.aging.peakDays}, held to day ${d.aging.holdUntilDays}, then −${Math.round(d.aging.decayPerDay * 100)}% a day. Spoils at ${d.aging.spoilAtDays} days.${d.aging.washedRind ? ' Needs rind washing on the racks.' : ''}</p>`
            : `<p><b>Freshness:</b> ${d.aging.freshnessMultipliers.map((m, i) => `day ${i} ×${m}`).join(', ')}, spoils on day ${d.aging.spoilAfterDays}.</p>`,
          ...entry.valueNotes.map((v) => `<p>${v}</p>`),
        ];
        return lines.join('');
      };

      const renderQuiz = (el: HTMLElement, entry: CodexEntry): void => {
        const s = game.state;
        const a = game.quiz && game.quiz.cheeseId === entry.cheeseId ? game.quiz : null;
        if (!a) {
          const cost = Codex.quizCost(s, entry.cheeseId);
          const block = Codex.quizBlock(s, entry.cheeseId);
          el.innerHTML = `
            <p>${CODEX.QUESTIONS} questions, four answers each. Get all ${CODEX.PASS_CORRECT} right in one sitting to learn the recipe. Each question pays <b>1 cosmic gem</b> the first time you answer it correctly. Attempts so far: ${s.codex.attempts[entry.cheeseId] ?? 0}.</p>
            <div class="row"><button class="btn primary start">${cost ? `Retest · ${cost} coins` : 'Start the quiz (free)'}</button><span class="muted">${block ?? ''}</span></div>`;
          const btn = $<HTMLButtonElement>(el, '.start');
          btn.disabled = !!block;
          btn.onclick = () => {
            if (game.startQuiz(entry.cheeseId)) api.rerender();
          };
          return;
        }
        if (a.done) {
          el.innerHTML = `
            <p class="${a.passed ? 'good' : 'bad'}"><b>${a.correct} / ${a.questions.length} correct.</b> ${a.passed ? `You learned ${CHEESES[entry.cheeseId].name}.` : 'Not quite. Read the entry again and retest.'}</p>
            <p>Gems earned this attempt: ${a.gemsEarned}.</p>
            <div class="row"><button class="btn primary ok">Back to the entry</button></div>`;
          $<HTMLButtonElement>(el, '.ok').onclick = () => {
            game.abandonQuiz();
            tab = 'story';
            api.rerender();
          };
          return;
        }
        const q = a.questions[a.current];
        el.innerHTML = `
          <div class="muted">Question ${a.current + 1} of ${a.questions.length} · ${a.correct} correct so far</div>
          <div class="quiz-q">${q.q}</div>
          <div class="opts"></div>
          <div class="row"><button class="btn primary next" style="display:none">Next</button><span class="muted result"></span></div>`;
        const opts = $(el, '.opts');
        const next = $<HTMLButtonElement>(el, '.next');
        const result = $(el, '.result');
        const buttons: HTMLButtonElement[] = [];
        q.options.forEach((text, i) => {
          const b = document.createElement('button');
          b.className = 'btn quiz-opt';
          b.textContent = `${String.fromCharCode(65 + i)}) ${text}`;
          b.onclick = () => {
            if (q.picked !== null) return;
            const r = game.answerQuiz(i);
            buttons.forEach((bb, j) => {
              bb.disabled = true;
              if (j === q.answer) bb.classList.add('right');
              if (j === i && !r.correct) bb.classList.add('wrong');
            });
            result.textContent = r.correct ? (r.gems ? 'Correct! +1 gem.' : 'Correct (gem already collected).') : 'Wrong.';
            result.className = r.correct ? 'good' : 'bad';
            next.style.display = '';
            next.textContent = a.current + 1 >= a.questions.length ? 'See the result' : 'Next';
          };
          buttons.push(b);
          opts.appendChild(b);
        });
        next.onclick = () => {
          game.nextQuizQuestion();
          api.rerender();
        };
      };

      renderChapters();
      renderPage();
      return {
        update() {
          const coins = chapters.querySelector('.muted');
          if (coins) coins.textContent = `Coins ${fmt(game.state.gold)} · Gems ${game.state.gems}`;
        },
      };
    },
  });
}
